// Reference material attached to a learner activity.
//
// An instructor planning a class usually has the source material to hand — a
// reading, a syllabus extract, a spec, notes from last term. Attaching it to
// the activity it belongs to means the next step in the workflow (the AI
// Prompt page, or a paste into an assistant) can work from the actual material
// rather than from a one-line summary of it.
//
// Files are read in the browser and only their extracted text is kept; nothing
// is uploaded anywhere. The bytes are not retained either — a plan carrying a
// dozen base64 PDFs would blow past the localStorage quota and make the saved
// JSON unshareable.

import { readZipEntryText } from './zipReader';
import { extractPdfText } from './pdfText';

/** Accept list. Extensions and MIME types both, so iOS's picker behaves. */
export const REFERENCE_FILE_ACCEPT = [
  '.txt', '.md', '.markdown', '.json', '.pdf', '.doc', '.docx',
  'text/plain', 'text/markdown', 'application/json', 'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
].join(',');

/**
 * Per-file cap on extracted text. Reference material lives in localStorage
 * alongside the plan and travels inside the saved JSON, so a 300-page PDF has
 * to be trimmed or it takes the whole autosave down with it. 40k characters is
 * roughly 6,000 words — more than enough context for a lesson activity.
 */
export const MAX_REFERENCE_TEXT_CHARS = 40000;

/** Cap across every attachment in the plan, for the same reason. */
export const MAX_TOTAL_REFERENCE_CHARS = 200000;

/** Files bigger than this are refused before they are read. */
export const MAX_REFERENCE_FILE_BYTES = 15 * 1024 * 1024;

const EXTENSION_KINDS = {
  txt: 'text',
  text: 'text',
  md: 'markdown',
  markdown: 'markdown',
  json: 'json',
  pdf: 'pdf',
  docx: 'docx',
  doc: 'doc'
};

export const getFileExtension = (name = '') => {
  const match = /\.([a-z0-9]+)$/i.exec(String(name).trim());
  return match ? match[1].toLowerCase() : '';
};

export const getReferenceKind = (name = '', mimeType = '') => {
  const byExtension = EXTENSION_KINDS[getFileExtension(name)];
  if (byExtension) return byExtension;
  if (/pdf/i.test(mimeType)) return 'pdf';
  if (/wordprocessingml/i.test(mimeType)) return 'docx';
  if (/msword/i.test(mimeType)) return 'doc';
  if (/json/i.test(mimeType)) return 'json';
  if (/markdown/i.test(mimeType)) return 'markdown';
  if (/^text\//i.test(mimeType)) return 'text';
  return 'unknown';
};

export const KIND_LABELS = {
  text: 'Text',
  markdown: 'Markdown',
  json: 'JSON',
  pdf: 'PDF',
  docx: 'Word',
  doc: 'Word (legacy)',
  link: 'Link',
  unknown: 'File'
};

/**
 * Only http(s) links are kept.
 *
 * A reference travels into an exported HTML file where it becomes a real
 * anchor, and into a prompt someone pastes into an assistant. `javascript:`,
 * `data:` and friends have no business in either.
 */
export const normalizeReferenceUrl = (value) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  // A bare domain is what people paste; without a scheme the browser reads it
  // as a path relative to lessonplannerone.com.
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    if (!url.hostname) return '';
    return url.href;
  } catch (error) {
    return '';
  }
};

/** What to call a link the instructor gave no title for. */
export const defaultLinkName = (url) => {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/+$/, '');
    return `${parsed.hostname.replace(/^www\./, '')}${path}` || parsed.hostname;
  } catch (error) {
    return url;
  }
};

/**
 * A link record, which lives in the same list as the attached files.
 *
 * One list rather than two: everything downstream — the saved JSON, the HTML
 * export's appendix, the hand-off to the AI Prompt page and the generated
 * prompt — already walks `referenceFiles`, and an instructor thinks of "the
 * reading" and "the video" as the same kind of thing.
 *
 * @returns {object|null} the record, or null when the address is unusable
 */
export const createReferenceLink = ({ url, name, note } = {}) => {
  const href = normalizeReferenceUrl(url);
  if (!href) return null;
  const label = typeof name === 'string' && name.trim() ? name.trim() : defaultLinkName(href);
  const description = typeof note === 'string' ? note.trim().slice(0, MAX_REFERENCE_TEXT_CHARS) : '';
  return {
    id: createId(),
    name: label,
    kind: 'link',
    url: href,
    mimeType: '',
    size: 0,
    addedAt: new Date().toISOString(),
    // A link's "text" is whatever the instructor wrote about it. The page
    // itself is not fetched — that would be a cross-origin request from the
    // browser for content nobody asked to download.
    text: description,
    charCount: description.length,
    truncated: false,
    note: ''
  };
};

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ref_${crypto.randomUUID()}`;
  }
  return `ref_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

/** Blob.text()/arrayBuffer() with a FileReader fallback for older Safari. */
const readAsText = (file) => {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file.'));
    reader.readAsText(file);
  });
};

const readAsArrayBuffer = (file) => {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read the file.'));
    reader.readAsArrayBuffer(file);
  });
};

const decodeXmlEntities = (value) => value
  .replace(/&#x([0-9a-f]+);/gi, (match, hex) => String.fromCodePoint(parseInt(hex, 16)))
  .replace(/&#(\d+);/g, (match, dec) => String.fromCodePoint(parseInt(dec, 10)))
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"')
  .replace(/&apos;/g, "'")
  .replace(/&amp;/g, '&');

/** WordprocessingML → plain text: paragraphs, tabs and breaks, nothing else. */
export const docxXmlToText = (xml) => {
  if (typeof xml !== 'string' || !xml) return '';
  const text = xml
    .replace(/<w:tab\b[^>]*\/?>/gi, '\t')
    .replace(/<w:br\b[^>]*\/?>/gi, '\n')
    .replace(/<w:cr\b[^>]*\/?>/gi, '\n')
    .replace(/<\/w:p>/gi, '\n')
    .replace(/<\/w:tr>/gi, '\n')
    .replace(/<\/w:tc>/gi, '\t')
    .replace(/<[^>]+>/g, '');
  return decodeXmlEntities(text)
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const extractDocxText = async (file) => {
  const buffer = await readAsArrayBuffer(file);
  const xml = await readZipEntryText(buffer, 'word/document.xml');
  if (xml === null) {
    return { text: '', note: 'That .docx has no document body — it may be damaged.' };
  }
  const text = docxXmlToText(xml);
  return {
    text,
    note: text ? '' : 'No text was found in that Word document.'
  };
};

const extractJsonText = (raw) => {
  try {
    return { text: JSON.stringify(JSON.parse(raw), null, 2), note: '' };
  } catch (error) {
    // Still useful as reference material, so keep it rather than refusing it.
    return { text: raw, note: 'That JSON did not parse; it was attached as plain text.' };
  }
};

export const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Read one file into a reference record.
 *
 * A file whose text cannot be extracted is still recorded — a named reading an
 * assistant is told about is worth more than nothing — with `note` explaining
 * why the words are missing.
 *
 * @param {File} file
 * @returns {Promise<object>} reference record
 * @throws when the file is too big or unreadable at the I/O level
 */
export const readReferenceFile = async (file) => {
  if (!file) throw new Error('No file was chosen.');
  if (file.size > MAX_REFERENCE_FILE_BYTES) {
    throw new Error(`"${file.name}" is ${formatFileSize(file.size)} — the limit is ${formatFileSize(MAX_REFERENCE_FILE_BYTES)}.`);
  }

  const kind = getReferenceKind(file.name, file.type);
  let text = '';
  let note = '';

  try {
    if (kind === 'pdf') {
      ({ text, note } = await extractPdfText(await readAsArrayBuffer(file)));
    } else if (kind === 'docx') {
      ({ text, note } = await extractDocxText(file));
    } else if (kind === 'doc') {
      note = 'Legacy .doc files cannot be read in the browser — re-save it as .docx to include its text.';
    } else if (kind === 'json') {
      ({ text, note } = extractJsonText(await readAsText(file)));
    } else if (kind === 'text' || kind === 'markdown') {
      text = (await readAsText(file)).replace(/\r\n/g, '\n').trim();
      if (!text) note = 'That file is empty.';
    } else {
      // Unknown extension: try it as text, since plenty of useful formats
      // (.csv, .rtf-ish notes, source files) are readable that way.
      const raw = await readAsText(file);
      // Control characters in the first few KB mean this is a binary format
      // being read as text, which yields mojibake rather than material.
      // eslint-disable-next-line no-control-regex
      if (/[\u0000-\u0008\u000E-\u001F]/.test(raw.slice(0, 2000))) {
        note = 'That file type is not supported — attach a .txt, .md, .json, .pdf or .docx file.';
      } else {
        text = raw.replace(/\r\n/g, '\n').trim();
      }
    }
  } catch (error) {
    note = error && error.message ? `Could not read this file: ${error.message}` : 'Could not read this file.';
    text = '';
  }

  const truncated = text.length > MAX_REFERENCE_TEXT_CHARS;
  const kept = truncated ? `${text.slice(0, MAX_REFERENCE_TEXT_CHARS)}\n…[truncated]` : text;

  return {
    id: createId(),
    name: file.name || 'reference',
    kind,
    mimeType: file.type || '',
    size: Number(file.size) || 0,
    addedAt: new Date().toISOString(),
    text: kept,
    charCount: kept.length,
    truncated,
    note
  };
};

/**
 * Clean a reference list loaded from a JSON file. Loaded plans are untrusted
 * input, so this drops anything that is not a plain record and re-derives the
 * fields the UI relies on.
 */
export const normalizeReferenceFiles = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
    .map((entry) => {
      // A link is identified by carrying a usable address, not by a filename —
      // and an address that does not survive normalizing (javascript:, a typo)
      // demotes the entry to an ordinary named reference rather than becoming
      // an anchor nobody vetted.
      const url = normalizeReferenceUrl(entry.url);
      const name = typeof entry.name === 'string' && entry.name.trim()
        ? entry.name.trim()
        : (url ? defaultLinkName(url) : 'reference');
      const text = typeof entry.text === 'string' ? entry.text.slice(0, MAX_REFERENCE_TEXT_CHARS) : '';
      const record = {
        id: typeof entry.id === 'string' && entry.id ? entry.id : createId(),
        name,
        kind: url ? 'link' : getReferenceKind(name, typeof entry.mimeType === 'string' ? entry.mimeType : ''),
        mimeType: typeof entry.mimeType === 'string' ? entry.mimeType : '',
        size: Number(entry.size) || 0,
        addedAt: typeof entry.addedAt === 'string' ? entry.addedAt : '',
        text,
        charCount: text.length,
        truncated: Boolean(entry.truncated) || (typeof entry.text === 'string' && entry.text.length > MAX_REFERENCE_TEXT_CHARS),
        note: typeof entry.note === 'string' ? entry.note : ''
      };
      if (url) record.url = url;
      return record;
    })
    .slice(0, 20);
};

/** Total extracted characters held across every activity in a plan. */
export const totalReferenceChars = (activities = []) =>
  (Array.isArray(activities) ? activities : []).reduce((total, activity) => {
    const files = Array.isArray(activity && activity.referenceFiles) ? activity.referenceFiles : [];
    return total + files.reduce((sum, file) => sum + (Number(file.charCount) || 0), 0);
  }, 0);

/**
 * The reference material as text an assistant can read, grouped by the
 * activity it was attached to. This is what makes the attachments useful in
 * the next step rather than just a list of filenames.
 */
export const buildReferenceMaterialText = (activities = []) => {
  const sections = [];
  (Array.isArray(activities) ? activities : []).forEach((activity, index) => {
    const files = Array.isArray(activity && activity.referenceFiles) ? activity.referenceFiles : [];
    if (!files.length) return;
    const entries = files.map((file) => {
      const header = `--- ${file.name} (${KIND_LABELS[file.kind] || 'File'}${file.truncated ? ', truncated' : ''}) ---`;
      if (file.kind === 'link') {
        // A link carries its address and whatever the instructor wrote about
        // it; the page itself is never fetched.
        return `${header}\n${file.url || ''}${file.text ? `\n${file.text}` : ''}`.trim();
      }
      const body = file.text ? file.text : `[No text could be extracted. ${file.note || ''}`.trim() + ']';
      return `${header}\n${body}`;
    });
    sections.push(`Reference material for Activity ${index + 1}:\n${entries.join('\n\n')}`);
  });
  return sections.join('\n\n');
};

/** One-line summary per activity, for the print sheet and the HTML export. */
export const summarizeReferenceFiles = (files = []) =>
  (Array.isArray(files) ? files : [])
    .map((file) => {
      if (file.kind === 'link') return `${file.name} (link)`;
      return `${file.name}${file.truncated ? ' (truncated)' : ''}`;
    })
    .join(', ');
